import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FiChevronDown, FiChevronUp } from "react-icons/fi";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../../auth/AuthProvider";
import { ModelInputs, GRID } from "../domain/types";
import { DEFAULT_INPUTS } from "../domain/defaults";
import { validateInputs } from "../domain/validation";
import { buildProjectPayload } from "../domain/payload";
import { Field } from "./Field";
import { ProjectStatusToggle } from "./ProjectStatusToggle";
import { GenProfileUpload } from "./GenProfileUpload";
import { LocationPicker, LocationData } from "./LocationPicker";
import { createProject, queueRun } from "../../../api/projects";
import { createClientProject } from "../../../api/partner";

// Core required fields (always required)
const CORE_REQUIRED_FIELDS = [
    "customer_id",
    "project_name",
    "latitude",
    "longitude",
    "timezone",
    "nameplate_capacity",
    "tech_type_id",
] as const;

// Additional fields required for US projects
const US_EXTRA_REQUIRED = ["state", "county"] as const;

// Combined for backwards compatibility
const REQUIRED_FIELDS = [...CORE_REQUIRED_FIELDS, ...US_EXTRA_REQUIRED] as const;

// Required sections shown by default
const REQUIRED_SECTIONS: { title: string; keys: readonly (keyof ModelInputs)[] }[] = [
    { title: "Basics", keys: ["project_name", "tech_type_id", "nameplate_capacity", "cod_date"] },
    { title: "Location Details", keys: ["city", "state", "county", "zip_code", "timezone"] },
];

// Advanced sections hidden behind toggle
const ADVANCED_SECTIONS: { title: string; keys: readonly (keyof ModelInputs)[]; note?: string }[] = [
    { title: "Grid Region Override", keys: ["eia_ba_code", "project_nerc_code"], note: "Override auto-detected BA and NERC regions if needed." },
    { title: "Dates", keys: ["decommision_date"] },
    { title: "Limits (optional)", keys: ["poi_limit", "project_additional_load"] },
    { title: "Power Purchase Agreement (PPA)", keys: ["ppa_flag", "ppa_percentage", "ppa_capacity"], note: "Toggle PPA to reveal related fields." },
    {
        title: "Battery", keys: [
            "battery_flag",
            "battery_capacity_mwh",
            "battery_charge_rate_mw",
            "battery_discharge_rate_mw",
            "battery_round_trip_eff",
            "battery_soc_min",
            "battery_chemistry",
            "battery_calendar_life_years",
            "battery_cycle_life",
            "battery_augmentation_flag",
            "battery_depth_of_discharge",
        ], note: "Toggle Battery to reveal related fields."
    },
];

export function ProjectCreateModal({
    isOpen,
    onClose,
    customerId,
    clientId,
    afterCreate,
    title = "Create New Project"
}: {
    isOpen: boolean;
    onClose: () => void;
    customerId: string;
    clientId?: string;
    afterCreate?: (projectId: string, name: string) => void;
    title?: string;
}) {
    const { token } = useAuth();
    const nav = useNavigate();
    const queryClient = useQueryClient();

    const [inputs, setInputs] = useState<ModelInputs>({ ...DEFAULT_INPUTS, customer_id: customerId });
    const [formError, setFormError] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showAdvanced, setShowAdvanced] = useState(false);

    // Re-key the form when customerId changes
    React.useEffect(() => setInputs(prev => ({ ...prev, customer_id: customerId })), [customerId]);

    const onField = (name: keyof ModelInputs, value: any) =>
        setInputs(prev => ({ ...prev, [name]: value }));

    // Determine required fields based on project location
    const requiredFields = useMemo(() => {
        const lat = typeof inputs.latitude === 'string' ? parseFloat(inputs.latitude) : inputs.latitude;
        const lng = typeof inputs.longitude === 'string' ? parseFloat(inputs.longitude) : inputs.longitude;
        const isUS = !isNaN(lat) && !isNaN(lng) && lng >= -170 && lng <= -60 && lat >= 18 && lat <= 72;
        // For US projects, require state/county; for EU/UK, only core fields
        return isUS || isNaN(lat)
            ? REQUIRED_FIELDS
            : CORE_REQUIRED_FIELDS;
    }, [inputs.latitude, inputs.longitude]);

    const { valid: isFormValid } = useMemo(
        () => validateInputs(inputs, { requiredKeys: requiredFields, requireGenIfExisting: true }),
        [inputs, requiredFields]
    );

    const handleSubmit: React.FormEventHandler = async (e) => {
        e.preventDefault();
        if (!token) return;
        setIsSubmitting(true);
        setFormError(null);
        try {
            const payload = buildProjectPayload({ ...inputs, customer_id: clientId || customerId });
            const data = clientId
                ? await createClientProject(clientId, payload, token)
                : await createProject(payload, token);

            // Queue run immediately — if it fails, still navigate (project was created)
            try {
                await queueRun(data.project_id, token);
                queryClient.setQueryData(["projectLatestRun", data.project_id], {
                    status: "queued",
                    summary_json: {},
                });
            } catch {
                // User can manually run from the detail page
            }

            if (afterCreate) {
                afterCreate(data.project_id, inputs.project_name);
            }

            onClose();
        } catch (err: any) {
            setFormError(err.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    const resetModal = () => setInputs({ ...DEFAULT_INPUTS, customer_id: customerId });

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <div className="w-full max-w-4xl max-h-[90vh] rounded-xl bg-surface-700 border border-border shadow-xl flex flex-col">
                <div className="p-6 pb-0">
                    <h3 className="mb-4 text-h4 text-text-primary">{title}</h3>
                </div>
                <form id="project-create-form" onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 space-y-8 pb-4">
                    <ProjectStatusToggle
                        isExisting={inputs.existing_project_flag}
                        onChange={(existing) =>
                            setInputs(p => ({ ...p, existing_project_flag: existing, sam_profile_flag: !existing }))
                        }
                    />

                    {/* Basics section */}
                    <section>
                        <h4 className="mb-3 text-body font-semibold text-quantum-400">Basics</h4>
                        <div className={GRID}>
                            {REQUIRED_SECTIONS[0].keys.map(k => (
                                <Field
                                    key={k}
                                    name={k as keyof ModelInputs}
                                    value={inputs[k as keyof ModelInputs]}
                                    onChange={onField}
                                    required={(REQUIRED_FIELDS as readonly (keyof ModelInputs)[]).includes(k as keyof ModelInputs)}
                                />
                            ))}
                        </div>
                    </section>

                    {/* Map-based location picker */}
                    <LocationPicker
                        latitude={inputs.latitude}
                        longitude={inputs.longitude}
                        onChange={(locationData: LocationData) => {
                            setInputs(prev => ({
                                ...prev,
                                latitude: locationData.latitude,
                                longitude: locationData.longitude,
                                state: locationData.state || prev.state,
                                county: locationData.county || prev.county,
                                city: locationData.city || prev.city,
                                zip_code: locationData.zipCode || prev.zip_code,
                                timezone: locationData.timezone || prev.timezone,
                                eia_ba_code: locationData.baCode || prev.eia_ba_code,
                                project_nerc_code: locationData.nercRegion || prev.project_nerc_code,
                            }));
                        }}
                    />

                    {/* Location Details section */}
                    <section className="relative">
                        <hr className="mb-8 border-t border-border" />
                        <h4 className="mb-3 text-body font-semibold text-quantum-400">Location Details</h4>
                        <div className={GRID}>
                            {REQUIRED_SECTIONS[1].keys.map(k => (
                                <Field
                                    key={k}
                                    name={k as keyof ModelInputs}
                                    value={inputs[k as keyof ModelInputs]}
                                    onChange={onField}
                                    required={(REQUIRED_FIELDS as readonly (keyof ModelInputs)[]).includes(k as keyof ModelInputs)}
                                />
                            ))}
                        </div>
                    </section>

                    {/* Toggle button for advanced options */}
                    <div>
                        <button
                            type="button"
                            onClick={() => setShowAdvanced(!showAdvanced)}
                            className="flex items-center text-quantum-400 hover:text-quantum-300 transition-colors"
                        >
                            {showAdvanced ? (
                                <>
                                    <FiChevronUp className="mr-1" /> Hide Advanced Options
                                </>
                            ) : (
                                <>
                                    <FiChevronDown className="mr-1" /> Show Advanced Options
                                </>
                            )}
                        </button>
                    </div>

                    {/* Advanced sections (conditionally rendered) */}
                    {showAdvanced && ADVANCED_SECTIONS.map((s) => (
                        <section key={s.title} className="relative">
                            <hr className="mb-8 border-t border-border" />
                            <h4 className="mb-3 text-body font-semibold text-quantum-400">{s.title}</h4>
                            {s.note && (
                                <p className="mb-3 text-body-sm text-text-muted italic">{s.note}</p>
                            )}
                            <div className={GRID}>
                                {s.keys.map(k => (
                                    <Field
                                        key={k}
                                        name={k as keyof ModelInputs}
                                        value={inputs[k as keyof ModelInputs]}
                                        onChange={onField}
                                        required={(REQUIRED_FIELDS as readonly (keyof ModelInputs)[]).includes(k as keyof ModelInputs)}
                                    />
                                ))}
                            </div>
                        </section>
                    ))}

                    <GenProfileUpload
                        value={inputs.gen_hour_profile_mw}
                        onChange={(rows) => onField("gen_hour_profile_mw", rows)}
                        required={inputs.existing_project_flag}
                        isExisting={inputs.existing_project_flag}
                    />

                    {formError && <p className="text-body-sm text-health-400 pb-4">{formError}</p>}
                </form>

                {/* Sticky footer with buttons */}
                <div className="sticky bottom-0 p-6 pt-4 bg-surface-700 border-t border-border flex justify-end gap-3">
                    <button
                        type="button"
                        onClick={() => {
                            resetModal();
                            onClose();
                        }}
                        disabled={isSubmitting}
                        className="btn-secondary"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        form="project-create-form"
                        disabled={isSubmitting || !isFormValid}
                        className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isSubmitting ? "Saving & Running…" : "Save & Run"}
                    </button>
                </div>
            </div>
        </div>
    );
}
